/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { accountDisplayName } from "../auth/accountLogin";
import { localUsername } from "../auth/admin/localUsername";

import styles from "./ParticipantFilter.module.css";

export interface HistoryParticipant {
  userId: string;
  name: string;
}

/** Match identities, never display names: two guests can have the same name. */
export function includesParticipants(
  participants: readonly HistoryParticipant[],
  selected: readonly string[],
): boolean {
  const ids = new Set(participants.map((person) => person.userId));
  return selected.every((id) => ids.has(id));
}

export function ParticipantFilter({
  participants,
  selected,
  onChange,
}: {
  participants: HistoryParticipant[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const root = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const dismiss = (event: PointerEvent): void => {
      if (
        event.target instanceof Node &&
        !root.current?.contains(event.target)
      ) {
        root.current?.removeAttribute("open");
      }
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && root.current?.open) {
        root.current.removeAttribute("open");
        root.current.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, []);
  const search = query.trim().toLocaleLowerCase();
  const options = participants.filter((person) =>
    `${person.name} ${person.userId} ${localUsername(person.userId)}`
      .toLocaleLowerCase()
      .includes(search),
  );
  return (
    <details ref={root} className={styles.root}>
      <summary className={styles.trigger}>
        {t("instance.ui.filter_participants")}
        {selected.length > 0 && (
          <span className={styles.count}>{selected.length}</span>
        )}
        <span aria-hidden="true">⌄</span>
      </summary>
      <div className={styles.panel}>
        <input
          type="search"
          aria-label={t("instance.ui.search_participants")}
          placeholder={t("instance.ui.search_participants")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <p className={styles.hint}>{t("instance.ui.participants_match_all")}</p>
        <div className={styles.options}>
          {options.map((person) => (
            <label
              key={person.userId}
              className={styles.option}
              aria-label={localUsername(person.userId)}
            >
              <input
                type="checkbox"
                aria-label={localUsername(person.userId)}
                checked={selected.includes(person.userId)}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, person.userId]
                      : selected.filter((id) => id !== person.userId),
                  )
                }
              />
              <span>
                <strong>
                  {accountDisplayName(person.userId, person.name)}
                </strong>
                <small>{localUsername(person.userId)}</small>
              </span>
            </label>
          ))}
          {options.length === 0 && (
            <p>{t("instance.ui.no_matching_participants")}</p>
          )}
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => onChange([])}
          >
            {t("instance.ui.clear_participants")}
          </button>
        )}
      </div>
    </details>
  );
}
